<template>
  <div :class="wrapClasses">
    <template v-if="type !== 'textarea'">
      <div v-if="$slots.prependbtn" v-show="slotReady" :class="[prefixIGCls+'-btn']" >
        <slot name="prependbtn"></slot>
      </div>
      <div v-if="$slots.prepend" v-show="slotReady" :class="[prefixIGCls+'-addon']">
        <slot name="prepend"></slot>
      </div>
      <span v-if="icon" :class="iconClasses" @click="$_onIconClick">
        <i :class="'aid-'+[icon]" class="aid"></i>
      </span>
      <div v-if="isGroup" :class="prefixCls">
        <input
          ref="input"
          :type="type"
          :class="inputClasses"
          :placeholder="placeholder"
          :disabled="disabled"
          :maxlength="maxlength"
          :readonly="readonly"
          :name="name"
          :value="currentValue"
          :number="number"
          :autofocus="autofocus"
          :clearable="clearable"
          @keyup.enter="$_onEnter"
          @keyup="$_onKeyup"
          @keypress="$_onKeypress"
          @keydown="$_onKeydown"
          @focus="$_onFocus"
          @blur="$_onBlur"
          @input="$_onInput"
          @change="$_onChange"/>
          <span v-if="isClear" :class="[prefixInputCls+'-close-icon']" @click="$_onClearIconClick">
            <i class="aid aid-close-circle text-muted"></i>
          </span>
      </div>
      <input v-else
             ref="input"
             :type="type"
             :class="inputClasses"
             :placeholder="placeholder"
             :disabled="disabled"
             :maxlength="maxlength"
             :readonly="readonly"
             :name="name"
             :value="currentValue"
             :number="number"
             :autofocus="autofocus"
             :clearable="clearable"
             @keyup.enter="$_onEnter"
             @keyup="$_onKeyup"
             @keypress="$_onKeypress"
             @keydown="$_onKeydown"
             @focus="$_onFocus"
             @blur="$_onBlur"
             @input="$_onInput"
             @change="$_onChange"/>
      <span v-if="isClear && !isGroup" :class="[prefixInputCls+'-close-icon']" @click="$_onClearIconClick">
        <i class="aid aid-close-circle text-muted"></i>
      </span>
      <div v-if="$slots.append" v-show="slotReady" :class="[prefixIGCls+'-addon']">
        <slot name="append"></slot>
      </div>
      <div v-if="$slots.appendbtn" v-show="slotReady" :class="[prefixIGCls+'-btn']">
        <slot name="appendbtn"></slot>
      </div>
    </template>
    <textarea
      v-else
      ref="textarea"
      :class="textareaClasses"
      :style="textareaStyles"
      :placeholder="placeholder"
      :disabled="disabled"
      :rows="rows"
      :maxlength="maxlength"
      :readonly="readonly"
      :name="name"
      :value="currentValue"
      :autofocus="autofocus"
      @keyup.enter="$_onEnter"
      @keyup="$_onKeyup"
      @keypress="$_onKeypress"
      @keydown="$_onKeydown"
      @focus="$_onFocus"
      @blur="$_onBlur"
      @input="$_onInput">
    </textarea>
  </div>
</template>
<script>
import { findComponentUpward, calcTextareaHeight } from '../../utils/assist'
import Emitter from '../../mixins/emitter'
const PREFIX_CLASS = 'input-wrapper'
const PREFIX_TEXTAREA_CLASS = 'input-textarea-wrapper'
const PREFIX_INPUT_CLASS = 'input'
const PREFIX_INPUT_GROUP_CLASS = 'input-group'
export default {
  name: 'TInput',
  mixins: [Emitter],
  props: {
    type: {
      type: String,
      validator(value) {
        return ['text', 'textarea', 'password'].includes(value)
      },
      default: 'text'
    },
    value: {
      type: [String, Number],
      default: ''
    },
    size: {
      type: String,
      validator(value) {
        return ['sm', 'lg'].includes(value)
      }
    },
    placeholder: {
      type: String,
      default: ''
    },
    maxlength: {
      type: Number
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    icon: String,
    iconPlacement: {
      type: String,
      validator(value) {
        return ['left', 'right'].includes(value)
      }
    },
    autosize: {
      type: [Boolean, Object],
      default: false
    },
    rows: {
      type: Number,
      default: 2
    },
    readonly: {
      type: Boolean,
      default: false
    },
    name: {
      type: String
    },
    number: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentValue: this.value,
      prefixCls: PREFIX_CLASS,
      prepend: true,
      prependbtn: true,
      append: true,
      appendbtn: true,
      slotReady: false,
      prefixInputCls: PREFIX_INPUT_CLASS,
      prefixIGCls: PREFIX_INPUT_GROUP_CLASS,
      textareaStyles: {}
    }
  },
  computed: {
    isGroup() {
      return (
        this.prepend ||
        this.prependbtn ||
        this.append ||
        this.appendbtn ||
        this.icon
      )
    },
    wrapClasses() {
      if (this.type !== 'textarea') {
        if (!this.isGroup) {
          return PREFIX_CLASS
        } else {
          return [
            `${PREFIX_INPUT_GROUP_CLASS}`,
            {
              [`${PREFIX_INPUT_GROUP_CLASS}--${this.size}`]:
                (this.prepend || this.append || this.icon) && !!this.size,
              [`${PREFIX_INPUT_GROUP_CLASS}--disabled`]: this.disabled
            }
          ]
        }
      } else {
        return PREFIX_TEXTAREA_CLASS
      }
    },
    inputClasses() {
      return [
        `${PREFIX_INPUT_CLASS}`,
        {
          [`${PREFIX_INPUT_CLASS}--${this.size}`]: !!this.size,
          [`${PREFIX_INPUT_CLASS}--disabled`]: this.disabled
        }
      ]
    },
    isClear () {
      return this.clearable && this.currentValue && !this.disabled && !this.readonly
    },
    iconClasses() {
      return [
        `input-group-icon`,
        {
          [`input-group-icon--${this.iconPlacement}`]: this.iconPlacement
        }
      ]
    },
    textareaClasses() {
      return [
        `${PREFIX_INPUT_CLASS}`,
        {
          [`${PREFIX_INPUT_CLASS}--disabled`]: this.disabled
        }
      ]
    }
  },
  watch: {
    value(val) {
      this.$_setCurrentValue(val)
    }
  },
  mounted() {
    if (this.type !== 'textarea') {
      this.prepend = this.$slots.prepend !== undefined
      this.append = this.$slots.append !== undefined
      this.prependbtn = this.$slots.prependbtn !== undefined
      this.appendbtn = this.$slots.appendbtn !== undefined
    } else {
      this.prepend = false
      this.append = false
      this.prependbtn = false
      this.appendbtn = false
    }
    this.slotReady = true
    this.$_resizeTextarea()
  },
  methods: {
    focus() {
      if (this.type === 'textarea') {
        this.$refs['textarea'].focus()
      } else {
        this.$refs['input'].focus()
      }
    },
    blur() {
      if (this.type === 'textarea') {
        this.$refs['textarea'].blur()
      } else {
        this.$refs['input'].blur()
      }
    },
    $_onEnter(event) {
      this.$emit('on-enter', event)
    },
    $_onKeydown(event) {
      this.$emit('on-keydown', event)
    },
    $_onKeypress(event) {
      this.$emit('on-keypress', event)
    },
    $_onKeyup(event) {
      this.$emit('on-keyup', event)
    },
    $_onIconClick(event) {
      this.$emit('on-icon-click', event)
    },
    $_onFocus(event) {
      this.$emit('on-focus', event)
    },
    $_onBlur(event) {
      this.$emit('on-blur', event)
      if (this.value!=='' && this.currentValue!=='' && this.value !== this.currentValue) {
        // 针对使用修饰符，比如number修饰符导致的currentValue和value不一致
        this.$_setCurrentValue(this.value)
      }
      if (this.number && this.value && Number.isNaN(Number(this.value))) {
        let _val = this.value.replace(/[^0-9]/gi, '')
        this.$emit('input', _val)
        this.$_setCurrentValue(_val)
      }
      if (
        !findComponentUpward(this, [
          'TDatePicker',
          'TTimePicker',
          'TCascader',
          'TSearch'
        ])
      ) {
        this.dispatch('TFormItem', 'on-form-blur', this.currentValue)
      }
    },
    $_onInput(event) {
      let value = event.target.value
      if (this.number && value && value !== '-') {
        value = Number(value)
        if (Number.isNaN(value)) {
          value = this.currentValue
          event.target.value = value
        }
      }
      this.$emit('input', value)
      this.$_setCurrentValue(value)
      this.$emit('on-change', event)
    },
    $_onChange(event) {
      this.$emit('on-input-change', event)
    },
    $_setCurrentValue(value) {
      if (value === this.currentValue) return
      this.$nextTick(() => {
        this.$_resizeTextarea()
      })
      this.currentValue = value
      if (
        !findComponentUpward(this, [
          'TDatePicker',
          'TTimePicker',
          'TCascader',
          'TSearch'
        ])
      ) {
        this.dispatch('TFormItem', 'on-form-change', value)
      }
    },
    $_resizeTextarea() {
      const autosize = this.autosize
      if (!autosize || this.type !== 'textarea') {
        return false
      }
      const minRows = autosize.minRows
      const maxRows = autosize.maxRows
      this.textareaStyles = calcTextareaHeight(
        this.$refs.textarea,
        minRows,
        maxRows
      )
    },
    $_onClearIconClick() {
      this.currentValue = ''
      this.$emit('input', '')
    }
  }
}
</script>



// WEBPACK FOOTER //
// input.vue?0ab16600